import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

/**
 * Hook for managing private notes during 1:1 sessions.
 * Notes are private per user - only the creator can see their notes.
 */
export function useOneOnOneNotes(sessionId, relationshipId) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteDocId, setNoteDocId] = useState(null);

  useEffect(() => {
    if (!sessionId || !user?.uid) {
      setNotes(null);
      setNoteDocId(null);
      setLoading(false);
      return;
    }

    // Query for this user's note for this session
    const q = query(
      collection(db, 'oneOnOneNotes'),
      where('sessionId', '==', sessionId),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          setNotes({ content: '' });
          setNoteDocId(null);
        } else {
          // Should only be one note per session per user
          const noteDoc = snapshot.docs[0];
          setNotes({
            id: noteDoc.id,
            ...noteDoc.data()
          });
          setNoteDocId(noteDoc.id);
        }
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useOneOnOneNotes] notes listener error:', err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sessionId, user?.uid]);

  const saveNotes = useCallback(async (content) => {
    if (!sessionId || !user?.uid) {
      throw new Error('Session and user required to save notes');
    }

    setSaving(true);
    try {
      if (noteDocId) {
        // Update existing note
        await updateDoc(doc(db, 'oneOnOneNotes', noteDocId), {
          content,
          updatedAt: new Date()
        });
      } else {
        // Create new note
        const docRef = await addDoc(collection(db, 'oneOnOneNotes'), {
          sessionId,
          relationshipId,
          userId: user.uid,
          content,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setNoteDocId(docRef.id);
      }
    } finally {
      setSaving(false);
    }
  }, [sessionId, relationshipId, user?.uid, noteDocId]);

  return {
    notes,
    loading,
    saving,
    saveNotes
  };
}
